import { useEffect, useMemo, useState } from 'react';
import { cachedGet, putCachedRead, REFERENCE_URLS } from '../offline/offlineApi';
import {
  normalizeWikiEntry, normalizeWikiPage, normalizeWikis, placeWikiPage, removeWikiPageFrom, sortWikiEntries,
} from '../util/wikis';

// All wikis incl. their entries (GET /WikiPage/GetAll) — one shared list for the whole app, same pattern as
// useRoleSettings: fetched at most once per page load and kept in this module, so the home tiles, the wiki
// pages, the offline placeholder and the home prefetch share one request. Offline through the reference
// cache. Admin changes patch the list locally (no refetch) and update the offline copy as well.
let shared = null;      // normalized list once loaded
let pending = null;     // in-flight promise
let version = 0;        // +1 per local change (a response requested before it is older than the list)
const listeners = new Set();

function publish(list) {
  shared = list;
  listeners.forEach(fn => fn(list));
}

// Local change after a successful API call: publish + keep the offline copy in line.
function commit(list) {
  version += 1;
  publish(list);
  putCachedRead(REFERENCE_URLS.wikis, list);
}

// force: fetch even if a list is loaded (reload) — the loaded list stays on screen until the answer arrives.
function load(force = false) {
  if (shared && !force) return Promise.resolve(shared);
  if (!pending) {
    const requestedAt = version;
    pending = cachedGet(REFERENCE_URLS.wikis)
      .then(data => {
        // A local change saved after this request was sent is newer than the answer: keep the list.
        if (shared && version !== requestedAt) return shared;
        const list = normalizeWikis(data);
        publish(list);
        return list;
      })
      .finally(() => { pending = null; });
  }
  return pending;
}

// Home page prefetch (offline copy, offlineApi.prefetchReferenceData): runs through the shared loader, so it
// reuses the request of the mounted home tiles instead of fetching in parallel.
export function preloadWikis() {
  return load().catch(() => null);
}

// Back online after "Erneut verbinden" (OfflineContext): load again and publish to every mounted consumer.
// On failure the previous list stays on screen and the next consumer retries.
export function reloadWikis() {
  return load(true).catch(() => null);
}

// Logout: forget the list, so the next login loads it again (and the cleared offline copy is written anew).
export function resetWikis() {
  shared = null;
  version += 1;
}

// Applies a local change to the loaded list. Without a loaded list (first load still running or failed) no
// partial list is published or cached: the list is fetched again once the running request is done.
function change(fn) {
  if (!shared) {
    version += 1;
    const running = pending ? pending.catch(() => null) : Promise.resolve();
    running.then(() => load(true)).catch(() => null);
    return;
  }
  const next = fn(shared);
  if (next) commit(next);
}

// Created or updated wiki (WikiPageResponse): placed with the server's position algorithm. A response
// without entries keeps the entries already known.
export function upsertWikiPage(rawPage) {
  change(list => {
    const page = normalizeWikiPage(rawPage);
    if (!Array.isArray(rawPage?.entries)) {
      page.entries = list.find(p => p.id === page.id)?.entries ?? [];
    }
    return placeWikiPage(list, page);
  });
}

export function removeWikiPage(id) {
  change(list => removeWikiPageFrom(list, id));
}

function patchEntries(wikiPageId, fn) {
  const pageId = Number(wikiPageId);
  change(list => (list.some(p => p.id === pageId)
    ? list.map(p => (p.id === pageId ? { ...p, entries: fn(p.entries) } : p))
    : null));
}

// POST /WikiEntry/Create answers with the full entry list of the wiki (inserting shifts the following ones).
export function setWikiEntries(wikiPageId, entries) {
  patchEntries(wikiPageId, () => sortWikiEntries(entries, Number(wikiPageId)));
}

export function upsertWikiEntry(rawEntry) {
  const entry = normalizeWikiEntry(rawEntry);
  patchEntries(entry.wikiPageId, entries =>
    sortWikiEntries([...entries.filter(e => e.id !== entry.id), entry], entry.wikiPageId));
}

export function removeWikiEntry(wikiPageId, entryId) {
  patchEntries(wikiPageId, entries => entries.filter(e => e.id !== Number(entryId)));
}

/**
 * Wikis for the home tiles, the wiki pages and the offline placeholder.
 * - wikis: all wikis by position, each with its entries by order number — empty until loaded
 * - byId: Map id → wiki
 * - loaded / failed: load state
 */
export default function useWikis() {
  const [list, setList] = useState(shared);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    const listener = (next) => {
      if (!alive) return;
      setList(next);
      setFailed(false);
    };
    listeners.add(listener);
    if (shared) setList(shared);
    else load().catch(() => { if (alive) setFailed(true); });
    return () => {
      alive = false;
      listeners.delete(listener);
    };
  }, []);

  return useMemo(() => {
    const wikis = list ?? [];
    return {
      wikis,
      byId: new Map(wikis.map(w => [w.id, w])),
      loaded: list != null,
      failed,
    };
  }, [list, failed]);
}
